import { CURRENT_SEASON } from '../config/season';

interface ESPNTeam {
  id: string;
  location: string;
  name: string;
  abbreviation: string;
  logo: string;
}

interface ESPNCompetitor {
  id: string;
  homeAway: 'home' | 'away';
  team: ESPNTeam;
  score?: string;
}

interface ESPNOdds {
  provider?: {
    name: string;
  };
  moneyline?: {
    home?: {
      current?: {
        odds: string;
      };
      close?: {
        odds: string;
      };
    };
    away?: {
      current?: {
        odds: string;
      };
      close?: {
        odds: string;
      };
    };
  };
  spread?: {
    home?: {
      current?: {
        line: string;
        odds: string;
      };
      close?: {
        line: string;
        odds: string;
      };
    };
    away?: {
      current?: {
        line: string;
        odds: string;
      };
      close?: {
        line: string;
        odds: string;
      };
    };
  };
  total?: {
    over?: {
      current?: {
        line: string;
        odds: string;
      };
      close?: {
        line: string;
        odds: string;
      };
    };
    under?: {
      current?: {
        line: string;
        odds: string;
      };
      close?: {
        line: string;
        odds: string;
      };
    };
  };
}

interface ESPNEvent {
  id: string;
  name: string;
  date: string;
  season: {
    year: number;
    type: number;
  };
  week: {
    number: number;
  };
  competitions: Array<{
    id: string;
    venue: {
      fullName: string;
      address: {
        city: string;
        state: string;
      };
    };
    competitors: ESPNCompetitor[];
    odds?: ESPNOdds[];
    status: {
      type: {
        id: string;
        name: string;
        state: string;
      };
      clock?: number;
      displayClock?: string;
    };
    broadcasts?: Array<{
      names: string[];
    }>;
  }>;
}

interface ESPNResponse {
  leagues: Array<{
    id: string;
    name: string;
    season: {
      year: number;
      type: number;
    };
  }>;
  events: ESPNEvent[];
}

export interface Game {
  id: string;
  espn_id: string;
  game_id: string;
  competition_id: string;
  week: number;
  season: number;
  home_team: string;
  away_team: string;
  home_team_abbr: string;
  away_team_abbr: string;
  home_team_logo: string;
  away_team_logo: string;
  game_date: string;
  home_spread: number;
  away_spread: number;
  home_score?: number;
  away_score?: number;
  is_final: boolean;
  status: string;
  venue: string;
}

class ESPNService {
  private baseUrl = 'https://site.api.espn.com/apis/site/v2/sports/football/nfl';

  async getWeekGames(week: number, year: number = CURRENT_SEASON): Promise<Game[]> {
    try {
      const url = `${this.baseUrl}/scoreboard?week=${week}&seasontype=2&year=${year}`;
      const response = await fetch(url);
      console.log('url:', url);
      if (!response.ok) {
        throw new Error(`ESPN API error: ${response.status}`);
      }

      const data: ESPNResponse = await response.json();
      return this.transformGames(data.events);
    } catch (error) {
      console.error('Error fetching ESPN data:', error);
      throw error;
    }
  }

  private transformGames(events: ESPNEvent[]): Game[] {
    return events.map(event => {
      const competition = event.competitions[0];
      const homeTeam = competition.competitors.find(c => c.homeAway === 'home');
      const awayTeam = competition.competitors.find(c => c.homeAway === 'away');
      
      if (!homeTeam || !awayTeam) {
        throw new Error(`Invalid competition data for event ${event.id}`);
      }

      // Extract team logos (ESPN provides direct logo URL)
      const homeTeamLogo = homeTeam.team.logo || '';
      const awayTeamLogo = awayTeam.team.logo || '';
      // Extract game_id and competition_id for the new odds API
      const game: Game = {
        id: event.id,
        espn_id: event.id,
        game_id: event.id, // ESPN event ID
        competition_id: competition.id, // ESPN competition ID
        week: event.week.number,
        season: event.season.year,
        home_team: `${homeTeam.team.location} ${homeTeam.team.name}`,
        away_team: `${awayTeam.team.location} ${awayTeam.team.name}`,
        home_team_abbr: homeTeam.team.abbreviation,
        away_team_abbr: awayTeam.team.abbreviation,
        home_team_logo: homeTeamLogo,
        away_team_logo: awayTeamLogo,
        game_date: event.date,
        home_spread: 0, // Will be populated by live odds service
        away_spread: 0, // Will be populated by live odds service
        home_score: homeTeam.score ? parseInt(homeTeam.score) : undefined,
        away_score: awayTeam.score ? parseInt(awayTeam.score) : undefined,
        is_final: competition.status.type.state === 'post',
        status: competition.status.type.name,
        venue: competition.venue.fullName
      };

      return game;
    });
  }


  async getCurrentWeek(): Promise<number> {
    try {
      const response = await fetch(`${this.baseUrl}/scoreboard`);
      const data: ESPNResponse = await response.json();
      
      if (data.events.length > 0) {
        return data.events[0].week.number;
      }
      
      return 1;
    } catch (error) {
      console.error('Error getting current week:', error);
      return 1;
    }
  }

  async getAllSeasonGames(year: number = CURRENT_SEASON): Promise<Game[]> {
    const allGames: Game[] = [];
    const totalWeeks = 18; // Regular season weeks 1-18
    
    console.log(`Loading all games for ${year} NFL season...`);
    
    for (let week = 1; week <= totalWeeks; week++) {
      try {
        console.log(`Fetching week ${week}/${totalWeeks}...`);
        const weekGames = await this.getWeekGames(week, year);
        allGames.push(...weekGames);
        
        // Add small delay to be respectful to ESPN's API
        await new Promise(resolve => setTimeout(resolve, 100));
      } catch (error) {
        console.error(`Error fetching week ${week}:`, error);
        // Continue with other weeks even if one fails
      }
    }
    
    console.log(`Loaded ${allGames.length} total games for ${year} season`);
    return allGames;
  }
}

export const espnService = new ESPNService();